import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useReducer,
} from 'react'
import { initialState, permissionsReducer } from './permissionsReducer'
import { IHasPermission, IPermissionsContext } from './types'

interface Props {
  children: ReactNode
}

const PermissionsContext = createContext<IPermissionsContext>(
  {} as IPermissionsContext,
)

export function PermissionsProvider({ children }: Props) {
  const [state, dispatch] = useReducer(permissionsReducer, initialState)

  function isRoot() {
    return state.isRoot
  }

  function getPermissions() {
    const companies = state.companies
    const permissions: any = []

    companies.forEach((company) => {
      const perm = company.agents.map((agent) => agent.actions)

      permissions.push({ company: company.name, agents: perm })
    })

    return companies
  }

  function getCompanies() {
    return state.companies
  }

  function isCompanyAdmin(companyName: string) {
    const companies = state.companies

    if (!companies) return false

    const company = companies.find((c) => c.name === companyName)
    return company?.isAdmin || false
  }

  const hasPermission = useCallback(
    ({ company, agent, permissions, actionPermissions }: IHasPermission) => {
      if (!company || !agent || !permissions.length) return false

      if (permissions.includes('p') || actionPermissions.includes('*'))
        return true

      const has = actionPermissions.every((p) => permissions.includes(p))

      return has
    },
    [],
  )

  return (
    <PermissionsContext.Provider
      value={{
        state: state || initialState,
        dispatch,
        getPermissions,
        isCompanyAdmin,
        isRoot,
        getCompanies,
        hasPermission,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  )
}

export function usePermissions(): IPermissionsContext {
  const context = useContext(PermissionsContext)
  return context
}
